// Service data — production copy.
// Pricing is intentionally never shown publicly. All CTAs route to Discovery Call.

export const SERVICES = [
  {
    slug: 'virtual-support',
    eyebrow: '01 — Reliable Virtual Support',
    name: 'Reliable Virtual Support',
    headline: 'Dependable operational support that reduces workload and creates consistency.',
    short:
      'Dependable operational support designed to reduce workload, improve responsiveness, and create consistency across daily business operations.',
    summary:
      'Reliable support across the day-to-day operations of your business — customer service, scheduling, follow-up, CRM, inbox, quality control, administrative work, and recruitment assistance. Built around your team and the way you serve your clients.',
    benefits: [
      'Reduced workload on owners and key team members',
      'Faster, more consistent responses to clients and leads',
      'Better visibility across daily operations',
      'Improved follow-through on the things that move the business forward',
    ],
    deliverables: [
      'Customer service',
      'Scheduling',
      'Lead follow-up',
      'CRM updates',
      'Inbox management',
      'Quality control follow-up',
      'Administrative support',
      'Recruitment assistance',
    ],
    process: [
      { step: '01', title: 'Discovery', body: 'A focused conversation to understand your operations, your team, and where support will have the most impact.' },
      { step: '02', title: 'Plan', body: 'A simple support plan with clear ownership, response standards, and the tools we will use.' },
      { step: '03', title: 'Onboarding', body: 'We align on processes, log in to the right systems, and begin shadowing the work for a short period.' },
      { step: '04', title: 'Steady support', body: 'I run the agreed scope each week, send a written update, and refine as the business grows.' },
    ],
    faq: [
      { q: 'Is this a virtual assistant service?', a: 'No. I work as an Operations & Systems Specialist. The support is delivered with ownership and accountability — focused on outcomes, not just task completion.' },
      { q: 'Do you work with my existing tools?', a: 'Yes. I work inside the tools your team already uses. If a change would clearly help the business, I will explain why and let you decide.' },
      { q: 'What kinds of businesses do you support?', a: 'Service-based businesses — cleaning, home services, property care, wellness, and similar local service brands.' },
      { q: 'How quickly can we start?', a: 'Most engagements begin within one to two weeks of the discovery call, depending on scope.' },
    ],
    image: 'https://images.unsplash.com/photo-1719760518176-e124a5bcd025',
  },
  {
    slug: 'website-design',
    eyebrow: '02 — Website Design & Development',
    name: 'Website Design & Development',
    headline: 'Professional websites built to establish credibility and support growth.',
    short:
      'Professional websites built to establish credibility, generate leads, and support business growth.',
    summary:
      'Clean, mobile-first websites built for service businesses that want to look as professional online as they are in person. Designed to support credibility, generate qualified leads, and grow with the business.',
    benefits: [
      'A professional, mobile-friendly presence that reflects your business',
      'Clear paths from visit to consultation request',
      'Integrated booking and CRM where it makes sense',
      'Foundations for SEO built into every page',
    ],
    deliverables: [
      'Business websites',
      'Landing pages',
      'Funnels',
      'Booking integrations',
      'CRM integration',
      'Mobile optimization',
      'Conversion optimization',
    ],
    process: [
      { step: '01', title: 'Discovery', body: 'We start with your audience, your services, and what you want each visitor to do.' },
      { step: '02', title: 'Structure', body: 'Sitemap, page outlines, and messaging draft for your review before any design begins.' },
      { step: '03', title: 'Design & build', body: 'Visual direction, refinement, and a full build with content, SEO, and analytics in place.' },
      { step: '04', title: 'Launch & support', body: 'We go live, monitor performance, and refine the highest-impact pages over the first 30 days.' },
    ],
    faq: [
      { q: 'How long does a project take?', a: 'Most websites are delivered within four to six weeks from kick-off, depending on scope and content readiness.' },
      { q: 'Do you write the copy?', a: 'Yes. Messaging and on-page copy are part of every engagement. If you prefer to write your own, I provide a structured brief and edits.' },
      { q: 'Will the site be easy to update?', a: 'Yes. Sites are built so your team can update content without needing technical help.' },
      { q: 'Will the site be SEO-ready?', a: 'Yes. Every site ships with the technical foundations needed to support local and on-page SEO.' },
    ],
    image: 'https://images.pexels.com/photos/13059862/pexels-photo-13059862.jpeg',
  },
  {
    slug: 'seo-optimization',
    eyebrow: '03 — SEO Optimization',
    name: 'SEO Optimization',
    headline: 'Helping local businesses become easier to find online.',
    short:
      'Search engine optimization strategies designed to improve local visibility and increase qualified inquiries.',
    summary:
      'A practical SEO programme focused on the work that helps local and service-based businesses become more visible to the people already searching for them.',
    benefits: [
      'Stronger local visibility on Google Search and Maps',
      'More qualified inquiries from buyers ready to book',
      'On-page foundations that support long-term growth',
      'Clear reporting tied to what actually matters',
    ],
    deliverables: [
      'Local SEO',
      'Google Business Profile optimisation',
      'Keyword research',
      'Service area pages',
      'Technical SEO',
      'Content optimisation',
    ],
    process: [
      { step: '01', title: 'Audit', body: 'A review of your current site, listings, and competitive landscape.' },
      { step: '02', title: 'Strategy', body: 'A clear plan mapped to your services and locations, prioritised by impact.' },
      { step: '03', title: 'Execute', body: 'On-page improvements, content updates, internal linking, and Google Business optimisation.' },
      { step: '04', title: 'Report & refine', body: 'A monthly report covering rankings, traffic, and the next set of priorities.' },
    ],
    faq: [
      { q: 'How long until I see results?', a: 'Local SEO improvements often begin to show in 60 to 90 days. Broader organic growth tends to compound over six to twelve months.' },
      { q: 'Do you guarantee rankings?', a: 'No reputable SEO partner guarantees specific rankings. I commit to the work, the reporting, and clear communication.' },
      { q: 'Is content writing included?', a: 'Yes — content production is part of every ongoing engagement, edited to match your brand voice.' },
    ],
    image: 'https://images.pexels.com/photos/7599590/pexels-photo-7599590.jpeg',
  },
  {
    slug: 'social-media-management',
    eyebrow: '04 — Social Media Management',
    name: 'Social Media Management',
    headline: 'Consistent content that keeps your business visible and relevant.',
    short:
      'Consistent content and brand management that helps businesses remain visible and relevant.',
    summary:
      'A measured, content-first approach to social media for service businesses — designed to build trust over time and support the wider sales process, rather than chase short-term attention.',
    benefits: [
      'A consistent presence that supports your brand',
      'Content that reinforces trust and credibility',
      'Less time spent on social by the founder',
      'A growing library of content you can reuse across channels',
    ],
    deliverables: [
      'Content creation',
      'Graphic design',
      'Caption writing',
      'Publishing',
      'Community management',
      'Review management',
    ],
    process: [
      { step: '01', title: 'Voice & pillars', body: 'We define how your brand sounds and the topics it covers consistently.' },
      { step: '02', title: 'Calendar', body: 'A rolling calendar of content aligned with your services and customer journey.' },
      { step: '03', title: 'Produce', body: 'Design, copy, and scheduling handled end to end.' },
      { step: '04', title: 'Review', body: 'A short monthly summary of what is working and where to focus next.' },
    ],
    faq: [
      { q: 'Which platforms do you manage?', a: 'Most commonly Instagram, Facebook, LinkedIn, and TikTok. We focus on the platforms where your audience already pays attention.' },
      { q: 'Do you create video content?', a: 'Yes — short-form video editing is included. We can also coach you on filming raw clips on a phone.' },
      { q: 'How is success measured?', a: 'We track meaningful indicators — saves, shares, profile visits, direct messages, and inquiries — alongside follower growth.' },
    ],
    image: 'https://images.pexels.com/photos/8360032/pexels-photo-8360032.jpeg',
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);
